import type { GetMarketTrendsSummaryResponseDto } from '@zg-rentals/ts-rental-growth-api-client';

import type { Temperature } from 'app/shared/types/Temperature';
import type { RentCount } from 'app/shared/types/RentCount';
import type { HistoricRentPrice } from 'app/shared/types/HistoricRentPrice';
import { AreaType } from 'app/shared/types/AreaType';
import type { Market } from 'app/shared/types/Market';
import pathsManifest from 'app/shared/utils/pathsManifest';

const AREA_TYPE_VALUES = new Set<string>(Object.values(AreaType));

// The webapi returns areaType in upper case (e.g. 'ZIP'), but our AreaType enum is lower case
// ('zip'). Normalize so comparisons like `areaType === AreaType.ZIP` keep working, and drop any
// value that isn't one we model.
const toAreaType = (areaType?: string | null): AreaType | null => {
  const normalized = areaType?.toLowerCase();
  return normalized && AREA_TYPE_VALUES.has(normalized) ? (normalized as AreaType) : null;
};

// The webapi returns date as a LocalDate object ({ year, month, day }); convert it to the
// ISO string that consumers like dateUtils.displayDate expect. (The legacy GraphQL path already
// returned a string, so pass those through unchanged.)
const toIsoDate = (date?: GetMarketTrendsSummaryResponseDto['date'] | string | null): string | null => {
  if (!date) {
    return null;
  }
  if (typeof date === 'string') {
    return date;
  }
  const { year, month, day } = date;
  if (year == null || month == null || day == null) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export type MarketTrends = {
  date: string | null;
  areaName: string | null;
  areaType: AreaType | null;
  medianRent: number | null;
  minRent: number | null;
  maxRent: number | null;
  monthlyChange: number | null;
  yearlyChange: number | null;
  availableRentals: number | null;
  avgDaysOnMarket: number | null;
  temperature: Temperature | null;
  rentCounts: Array<RentCount>;
  curYearRentPrices: Array<HistoricRentPrice>;
  prevYearRentPrices: Array<HistoricRentPrice>;
  medianNationalRent: number | null;
  zillowRentalsSrpUrl: string | null;
  resourceId: string;
  rentCompare: { medianRent: number; otherResourceId: string; areaName?: string };
  nearbyAreas: Array<{ name: string; resourceId: string }>;
  zipcodesInCity: Array<{ name: string; resourceId: string }>;
  nearbyAreaTrends: Array<Market>;
};

export const createMarketTrends = (
  res: GetMarketTrendsSummaryResponseDto,
  resourceId = '',
  otherResourceId = '',
): MarketTrends => ({
  date: toIsoDate(res?.date),
  areaName: res?.areaName ?? null,
  areaType: toAreaType(res?.areaType),
  medianRent: res?.summary?.medianRent ?? null,
  minRent: res?.rentHistogram?.minPrice ?? null,
  maxRent: res?.rentHistogram?.maxPrice ?? null,
  monthlyChange: res?.summary?.monthlyChange ?? null,
  yearlyChange: res?.summary?.yearlyChange ?? null,
  availableRentals: res?.summary?.availableRentals ?? null,
  avgDaysOnMarket: res?.summary?.avgDaysOnMarket ?? null,
  temperature: (res?.marketTemperature?.temperature as unknown as Temperature) ?? null,
  rentCounts: res?.rentHistogram?.priceAndCount ?? [],
  curYearRentPrices: res?.medianRentPriceOverTime?.currentYear ?? [],
  prevYearRentPrices: res?.medianRentPriceOverTime?.prevYear ?? [],
  medianNationalRent: res?.rentCompare?.medianRent ?? null,
  zillowRentalsSrpUrl: res?.zillowRentalsSrpUrl ?? null,
  resourceId,
  rentCompare: res?.rentCompare
    ? { areaName: res.rentCompare.areaName, medianRent: res.rentCompare.medianRent, otherResourceId }
    : { medianRent: 0, otherResourceId },
  nearbyAreas: res?.nearByAreas?.map(({ name, resourceName }) => ({ name, resourceId: resourceName })) ?? [],
  zipcodesInCity: res?.zipcodesInCity?.map(({ name, resourceName }) => ({ name, resourceId: resourceName })) ?? [],
  nearbyAreaTrends:
    res?.nearbyAreaTrends?.map((market) => ({
      url: `${pathsManifest.MarketTrendsPage}/${market.resourceId}/`,
      resourceId: market.resourceId,
      name: market.areaName,
      medianRent: market.medianRent,
      prices: market.medianRentPriceOverTime.map(({ price }) => price),
    })) ?? [],
});
